import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  CategoryType,
  useGetApiCategoryTypesQuery,
  usePostApiCategoryTypesMutation,
} from '../../../redux/store/api/api';
import { addMessage } from '../../../redux/store/layout/slice';
import { useAppDispatch } from '../../../redux/hooks';
import CustomDialog from '../../dialogs/CustomDialog';
import { defaultColor, defaultIcon } from '../defaultValues';
import Loader from '../../loader/Loader';

interface IAddCategoryTypeDialogProps {
  show: boolean;
  setActiveCategoryType: (categoryType: CategoryType) => void;
  onClose: () => void;
}

function AddCategoryTypeDialog({
  show,
  setActiveCategoryType,
  onClose,
}: IAddCategoryTypeDialogProps) {
  const { t: translation } = useTranslation();
  const [categoryTypeName, setCategoryTypeName] = useState<string>('');
  const [isValidCategoryTypeName, setIsValidCategoryTypeName] = useState(true);
  const categoryTypeNameInputRef = useRef<HTMLInputElement>(null);
  const [
    addCategoryType,
    {
      isError: postCategoryTypeIsError,
      isLoading,
      error: postCategoryTypeError,
    },
  ] = usePostApiCategoryTypesMutation();
  const {
    refetch,
    isError: getCategoryTypesIsError,
    error: getCategoryTypesError,
  } = useGetApiCategoryTypesQuery();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (show) {
      categoryTypeNameInputRef.current?.focus();
    }
  }, [show]);

  useEffect(() => {
    if (getCategoryTypesIsError) {
      dispatch(
        addMessage({
          id: 'GetCategoryTypesError',
          variant: 'danger',
          messageKeyBody:
            getCategoryTypesError && 'data' in getCategoryTypesError
              ? getCategoryTypesError.data?.messageKey
              : 'unknownError',
        }),
      );
    }
    if (postCategoryTypeIsError) {
      dispatch(
        addMessage({
          id: 'AddCategoryTypeError',
          variant: 'danger',
          messageKeyBody:
            postCategoryTypeError && 'data' in postCategoryTypeError
              ? postCategoryTypeError.data?.messageKey
              : 'unknownError',
        }),
      );
    }
  }, [getCategoryTypesIsError, postCategoryTypeIsError]);

  const handleAddCategoryType = (): void => {
    if (categoryTypeName.trim().length === 0) {
      setIsValidCategoryTypeName(false);
      return;
    }

    addCategoryType({
      categoryType: {
        name: categoryTypeName.trim(),
        colorHexCode: defaultColor,
        iconCssClass: defaultIcon,
      },
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'UpdateAttachmentSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        refetch();
        setCategoryTypeName('');
        onClose();
        setActiveCategoryType(result?.resultObject || {});
      });
  };

  return (
    <CustomDialog
      titleId='AddCategoryTypeDialog'
      show={show}
      closeFunction={() => {
        setCategoryTypeName('');
        setIsValidCategoryTypeName(true);
        onClose();
      }}
      closeTitle={translation('cancel')}
      actionFunction={handleAddCategoryType}
      actionTitle={translation('add')}
      actionButtonDisabled={isLoading}
      dialogTitle={translation('addCategoryType')}>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <p>{translation('fieldsAreRequiredLegend')}</p>
          <Form>
            <Form.Group className='mb-3' controlId='CategoryTypeName'>
              <Form.Label>{translation('categoryName')}*</Form.Label>
              <Form.Control
                ref={categoryTypeNameInputRef}
                required
                aria-describedby={
                  isValidCategoryTypeName
                    ? undefined
                    : 'CategoryTypeNameInputError'
                }
                isInvalid={!isValidCategoryTypeName}
                onChange={(e) => {
                  setCategoryTypeName(e.target.value);
                  if (e.target.value.trim()) {
                    setIsValidCategoryTypeName(true);
                  }
                }}
                type='text'
                value={categoryTypeName}
              />
              <Form.Control.Feedback
                id='CategoryTypeNameInputError'
                type='invalid'>
                {translation('fieldNotEmpty')}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </>
      )}
    </CustomDialog>
  );
}

export default AddCategoryTypeDialog;
